<script setup lang="ts">
import { MapboxMap, MapboxMarker, MapboxLayer } from '@studiometa/vue-mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { notifyError } from '@/utils/helpers'



const theme = useTheme()
const display = useDisplay()
const storeU = useUtilisateurStore()
const dialogConfirm = useTemplateRef('dialogConfirm')

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWdyaW1ldHJpZSIsImEiOiJjbTc3cjhrMTgxZGNqMnFxOGM4aGpjaW5hIn0.fUMzok-3aNnM9Zg7hpBKjQ'

// ── State ────────────────────────────────────────────────────────────────
const isLoading = ref(false)
const isSaving = ref(false)
const canEdit = computed(() => storeU.aAccèsPermission('site.PRSM.put.prsm.capture.capteurs'))
const siteCS = computed(() => storeU.siteSélectionné.lettresDappel)
const culturesDisponibles = ['Oignon', 'Laitue', 'Pomme de terre', 'Vigne', 'Crucifère']

const capteurs = ref<MDBPrismeCaptureCapteur[]>([])
const filter = ref('')
const showDésactivés = ref(false)

const filteredCapteurs = computed(() => {
    const q = filter.value.trim().toLowerCase()
    const list = showDésactivés.value ? capteurs.value : capteurs.value.filter(c => !c.désactivé)
    if (!q) return list
    return list.filter(c =>
        c.nom.toLowerCase().includes(q) ||
        c.thingsBoardId.toLowerCase().includes(q) ||
        c.dernierChampNom.toLowerCase().includes(q) ||
        c.dernierClientNom.toLowerCase().includes(q)
    )
})

// ── Dialog ────────────────────────────────────────────────────────────────
const showDialog = ref(false)
const isEditMode = ref(false)
const isSavingDialog = ref(false)
const isLocating = ref(false)

declare interface CapteurForm {
    id: string
    thingsBoardId: string
    nom: string
    dernièreLatitude: number | null
    dernièreLongitude: number | null
    dernierChampId: string
    dernierChampNom: string
    dernierRangOrdre: number
    désactivé: boolean
    cultures: string[]
}

const emptyForm = (): CapteurForm => ({
    id: '',
    thingsBoardId: '',
    nom: '',
    dernièreLatitude: null,
    dernièreLongitude: null,
    dernierChampId: '',
    dernierChampNom: '',
    dernierRangOrdre: 0,
    désactivé: false,
    cultures: [],
})

const form = ref<CapteurForm>(emptyForm())

// ── Clients / Champ autocomplete ──────────────────────────────────────────
const clients = ref<string[]>([])
const selectedClient = ref<string | null>(null)
const champPhrase = ref('')
const champSuggestions = ref<MDBPrismeChamp[]>([])
const selectedChamp = ref<MDBPrismeChamp | null>(null)
const champValue = ref<MDBPrismeChamp | string | null>(null)
const isLoadingChamps = ref(false)
// Tracks whether the current lat/lng were auto-filled from a champ selection (vs. manually set)
const coordsFromChamp = ref(false)

async function loadClients() {
    if (clients.value.length > 0) return
    try {
        const res = await apiProphet.get(`prsm/${siteCS.value}/clients`)
        clients.value = res.data as string[]
    } catch (e) {
        notifyError(e)
    }
}

let champSearchTimeout: ReturnType<typeof setTimeout> | null = null
async function onChampInput(phrase: string | null) {
    if (!phrase) { champSuggestions.value = []; return }
    champPhrase.value = phrase
    champSuggestions.value = []
    if (!selectedClient.value || phrase.trim().length < 1) return
    if (champSearchTimeout) clearTimeout(champSearchTimeout)
    champSearchTimeout = setTimeout(async () => {
        isLoadingChamps.value = true
        try {
            const res = await apiProphet.get(`prsm/${siteCS.value}/champs/completion`, {
                params: { clientLD: selectedClient.value, phrase },
            })
            champSuggestions.value = res.data as MDBPrismeChamp[]
        } catch (e) {
            notifyError(e)
        } finally {
            isLoadingChamps.value = false
        }
    }, 300)
}

function onChampSelect(value: MDBPrismeChamp | string | null) {
    champValue.value = value
    if (!value) {
        selectedChamp.value = null
        form.value.dernierChampId = ''
        form.value.dernierChampNom = ''
    } else if (typeof value === 'string') {
        // Free-text entry — no champ ID; clear any coordinates that were auto-filled from the previous champ
        if (coordsFromChamp.value) {
            form.value.dernièreLatitude = null
            form.value.dernièreLongitude = null
            coordsFromChamp.value = false
        }
        selectedChamp.value = null
        form.value.dernierChampId = ''
        form.value.dernierChampNom = value
    } else {
        selectedChamp.value = value
        form.value.dernierChampId = value.id
        form.value.dernierChampNom = value.nom
        if (value.latitude !== 0 || value.longitude !== 0) {
            form.value.dernièreLatitude = value.latitude
            form.value.dernièreLongitude = value.longitude
            mapCenter.value = [value.longitude, value.latitude]
            coordsFromChamp.value = true
        }
    }
}

watch(selectedClient, (newClient) => {
    if (selectedChamp.value?.sitePrsmLD === newClient) return
    selectedChamp.value = null
    champValue.value = null
    champPhrase.value = ''
    champSuggestions.value = []
    form.value.dernierChampId = ''
    form.value.dernierChampNom = ''
    if (coordsFromChamp.value) {
        form.value.dernièreLatitude = null
        form.value.dernièreLongitude = null
        coordsFromChamp.value = false
    }
})

// ── Map ──────────────────────────────────────────────────────────────────
const mapCenter = ref<[number, number]>([-73.5, 46.5])
const isLocatingNearby = ref(false)

// ── Champs GeoJSON overlay ───────────────────────────────────────────────
const showChampsOverlay = ref(false)
const isLoadingGeoJson = ref(false)
const champsGeoJson = ref<any>(null)

const producerColors = [
    '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
    '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FF5722',
    '#009688', '#673AB7', '#CDDC39', '#795548', '#607D8B',
    '#FFC107', '#03A9F4', '#76FF03', '#FF4081', '#536DFE',
]

const producerColorMap = computed(() => {
    const map = new Map<string, string>()
    if (!champsGeoJson.value) return map
    const unique = [...new Set(
        champsGeoJson.value.features
            .map((f: any) => f.properties?.fast_carto)
            .filter((v: any) => v != null),
    )] as string[]
    unique.forEach((producer, i) => map.set(producer, producerColors[i % producerColors.length]))
    return map
})

function buildFillColorExpr() {
    const entries = [...producerColorMap.value.entries()]
    if (entries.length === 0) return '#FFEB3B'
    const expr: any[] = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) expr.push(key, color)
    expr.push('#FFEB3B')
    return expr
}

function buildOutlineColorExpr() {
    const entries = [...producerColorMap.value.entries()]
    if (entries.length === 0) return '#F57F17'
    const expr: any[] = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) expr.push(key, color)
    expr.push('#F57F17')
    return expr
}

const champsLabelsGeoJson = computed(() => {
    if (!champsGeoJson.value) return { type: 'FeatureCollection', features: [] }
    const features = champsGeoJson.value.features.map((feature: any) => {
        const coords = feature.geometry?.coordinates
        if (!coords?.length) return null
        const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
        if (!ring?.length) return null
        let sumX = 0, sumY = 0
        for (const pt of ring) { sumX += pt[0]; sumY += pt[1] }
        const label = feature.properties?.IDENCHAMP || ''
        if (!label) return null
        return {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [sumX / ring.length, sumY / ring.length] },
            properties: { IDENCHAMP: label, fast_carto: feature.properties?.fast_carto ?? null },
        }
    }).filter(Boolean)
    return { type: 'FeatureCollection', features }
})

const champsGroupLabelsGeoJson = computed(() => {
    if (!champsGeoJson.value) return { type: 'FeatureCollection', features: [] }
    const groups = new Map<string, number[][]>()
    for (const feature of champsGeoJson.value.features) {
        const producer = feature.properties?.fast_carto
        if (!producer) continue
        const coords = feature.geometry?.coordinates
        if (!coords?.length) continue
        const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
        if (!ring?.length) continue
        let sumX = 0, sumY = 0
        for (const pt of ring) { sumX += pt[0]; sumY += pt[1] }
        if (!groups.has(producer)) groups.set(producer, [])
        groups.get(producer)!.push([sumX / ring.length, sumY / ring.length])
    }
    const features = [...groups.entries()].map(([producer, centroids]) => ({
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [
                centroids.reduce((s, c) => s + c[0], 0) / centroids.length,
                centroids.reduce((s, c) => s + c[1], 0) / centroids.length,
            ],
        },
        properties: { fast_carto: producer },
    }))
    return { type: 'FeatureCollection', features }
})

const emptyFc = { type: 'FeatureCollection', features: [] }

const champsFillLayerOptions = computed(() => ({
    type: 'fill',
    source: { type: 'geojson', data: champsGeoJson.value ?? emptyFc },
    paint: { 'fill-color': buildFillColorExpr(), 'fill-opacity': 0.35, 'fill-outline-color': buildOutlineColorExpr() },
}))

const champsOutlineLayerOptions = computed(() => ({
    type: 'line',
    source: { type: 'geojson', data: champsGeoJson.value ?? emptyFc },
    paint: { 'line-color': buildOutlineColorExpr(), 'line-width': 1.5 },
}))

const champsLabelLayerOptions = computed(() => ({
    type: 'symbol',
    source: { type: 'geojson', data: champsLabelsGeoJson.value },
    minzoom: 12,
    layout: {
        'text-field': ['get', 'IDENCHAMP'],
        'text-size': 11,
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-optional': true,
    },
    paint: { 'text-color': '#000000', 'text-halo-color': '#FFFFFF', 'text-halo-width': 1.5 },
}))

const champsGroupLabelLayerOptions = computed(() => {
    const entries = [...producerColorMap.value.entries()]
    let textColor: any = '#FFFFFF'
    if (entries.length > 0) {
        textColor = ['match', ['get', 'fast_carto']]
        for (const [key, color] of entries) textColor.push(key, color)
        textColor.push('#FFFFFF')
    }
    return {
        type: 'symbol',
        source: { type: 'geojson', data: champsGroupLabelsGeoJson.value },
        layout: {
            'text-field': ['get', 'fast_carto'],
            'text-size': 14,
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-optional': true,
            'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
            'text-letter-spacing': 0.05,
        },
        paint: { 'text-color': textColor, 'text-halo-color': 'rgba(0,0,0,0.75)', 'text-halo-width': 2 },
    }
})

async function toggleChampsOverlay() {
    showChampsOverlay.value = !showChampsOverlay.value
    if (showChampsOverlay.value && !champsGeoJson.value) {
        isLoadingGeoJson.value = true
        try {
            const res = await apiProphet.get(`prsm/${siteCS.value}/champs/geojson`)
            const jsons: any[] = Array.isArray(res.data) ? res.data : [res.data]
            const features: any[] = []
            for (const geojson of jsons) {
                if (geojson.type === 'FeatureCollection' && geojson.features)
                    features.push(...geojson.features)
                else if (geojson.type === 'Feature')
                    features.push(geojson)
            }
            champsGeoJson.value = { type: 'FeatureCollection', features }
        } catch (e) {
            notifyError(e)
            showChampsOverlay.value = false
        } finally {
            isLoadingGeoJson.value = false
        }
    }
}

async function findNearbyChamp(lat: number, lng: number) {
    try {
        const res = await apiProphet.get(`prsm/${siteCS.value}/champs/proche`, {
            params: { longitude: lng, latitude: lat },
        })
        const champ = res.data as MDBPrismeChamp
        if (champ?.id) {
            selectedClient.value = champ.sitePrsmLD
            champSuggestions.value = [champ]
            selectedChamp.value = champ
            champValue.value = champ
            champPhrase.value = champ.nom
            form.value.dernierChampId = champ.id
            form.value.dernierChampNom = champ.nom
            if (champ.latitude !== 0 || champ.longitude !== 0) {
                form.value.dernièreLatitude = champ.latitude
                form.value.dernièreLongitude = champ.longitude
                mapCenter.value = [champ.longitude, champ.latitude]
                coordsFromChamp.value = true
            }
        }
    } catch {
        // silently ignore — nearby champ is best-effort
    }
}

function onMapClick(e: any) {
    const { lng, lat } = e.lngLat
    form.value.dernièreLatitude = parseFloat(lat.toFixed(6))
    form.value.dernièreLongitude = parseFloat(lng.toFixed(6))
    coordsFromChamp.value = false

    // If the champs overlay is active, check if the click landed inside a polygon
    if (!showChampsOverlay.value) return
    const map = e.target
    if (!map) return
    const features = map.queryRenderedFeatures(e.point, { layers: ['capteur-champs-fill'] })
    if (!features?.length) return

    const props = features[0].properties
    const clientLD: string = props?.fast_carto ?? ''
    const champNom: string = props?.IDENCHAMP ?? ''
    const champId: string = props?.ChampId ?? ''
    const x: number = props?.X ?? 0
    const y: number = props?.Y ?? 0

    if (!champNom) return

    const champ: MDBPrismeChamp = {
        id: champId,
        siteId: '',
        sitePrsmLD: clientLD,
        nom: champNom,
        latitude: y,
        longitude: x,
        acres: props?.Acres ?? 0,
        hectares: props?.Hectares ?? 0,
        perimetre: props?.Perimetre ?? 0,
    }
    selectedClient.value = clientLD
    champSuggestions.value = [champ]
    selectedChamp.value = champ
    champValue.value = champ
    champPhrase.value = champNom
    form.value.dernierChampId = champId
    form.value.dernierChampNom = champNom
    if (x !== 0 || y !== 0) { mapCenter.value = [x, y]; coordsFromChamp.value = true }
}

function locateMe() {
    if (!navigator.geolocation) {
        notifyError('La géolocalisation n\'est pas supportée par ce navigateur.')
        return
    }
    isLocating.value = true
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            form.value.dernièreLatitude = parseFloat(pos.coords.latitude.toFixed(6))
            form.value.dernièreLongitude = parseFloat(pos.coords.longitude.toFixed(6))
            mapCenter.value = [pos.coords.longitude, pos.coords.latitude]
            coordsFromChamp.value = false
            isLocating.value = false
        },
        (err) => {
            isLocating.value = false
            notifyError(`Impossible d'obtenir la position : ${err.message}`)
        },
        { enableHighAccuracy: true, timeout: 10000 }
    )
}

function autoLocateAndFillChamp() {
    if (!navigator.geolocation) return
    isLocatingNearby.value = true
    navigator.geolocation.getCurrentPosition(
        async (pos) => {
            const lat = parseFloat(pos.coords.latitude.toFixed(6))
            const lng = parseFloat(pos.coords.longitude.toFixed(6))
            form.value.dernièreLatitude = lat
            form.value.dernièreLongitude = lng
            mapCenter.value = [lng, lat]
            await findNearbyChamp(lat, lng)
            isLocatingNearby.value = false
        },
        () => { isLocatingNearby.value = false },
        { enableHighAccuracy: true, timeout: 8000 }
    )
}
const markerCoords = computed<[number, number] | null>(() => {
    if (form.value.dernièreLatitude !== null && form.value.dernièreLongitude !== null)
        return [form.value.dernièreLongitude, form.value.dernièreLatitude]
    return null
})

// ── CRUD ─────────────────────────────────────────────────────────────────
async function getData() {
    isLoading.value = true
    try {
        const res = await apiProphet.get(`prsm/${siteCS.value}/captures/capteurs`)
        capteurs.value = res.data as MDBPrismeCaptureCapteur[]
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

function openCreate() {
    form.value = emptyForm()
    selectedClient.value = null
    selectedChamp.value = null
    champValue.value = null
    champPhrase.value = ''
    champSuggestions.value = []
    coordsFromChamp.value = false
    isEditMode.value = false
    showDialog.value = true
    loadClients()
    autoLocateAndFillChamp()
}

function openEdit(capteur: MDBPrismeCaptureCapteur) {
    form.value = {
        id: capteur.id,
        thingsBoardId: capteur.thingsBoardId,
        nom: capteur.nom,
        dernièreLatitude: capteur.dernièreLatitude || null,
        dernièreLongitude: capteur.dernièreLongitude || null,
        dernierChampId: capteur.dernierChampId,
        dernierChampNom: capteur.dernierChampNom,
        dernierRangOrdre: capteur.dernierRangOrdre,
        désactivé: capteur.désactivé ?? false,
        cultures: capteur.cultures ?? [],
    }
    if (capteur.dernièreLongitude && capteur.dernièreLatitude)
        mapCenter.value = [capteur.dernièreLongitude, capteur.dernièreLatitude]
    selectedClient.value = capteur.dernierClientNom || null
    // Coordinates are considered "from champ" only when a champ was previously associated
    coordsFromChamp.value = !!capteur.dernierChampId
    if (capteur.dernierChampId) {
        const champ: MDBPrismeChamp = {
            id: capteur.dernierChampId,
            siteId: '',
            sitePrsmLD: capteur.dernierClientNom,
            nom: capteur.dernierChampNom,
            latitude: capteur.dernièreLatitude ?? 0,
            longitude: capteur.dernièreLongitude ?? 0,
            acres: 0,
            hectares: 0,
            perimetre: 0,
        }
        champSuggestions.value = [champ]
        selectedChamp.value = champ
        champValue.value = champ
        champPhrase.value = capteur.dernierChampNom
    } else {
        selectedChamp.value = null
        champValue.value = capteur.dernierChampNom || null
        champPhrase.value = ''
        champSuggestions.value = []
    }
    isEditMode.value = true
    showDialog.value = true
    loadClients()
}

async function saveDialog() {
    if (!form.value.nom.trim()) {
        notifyError('Le nom est requis.')
        return
    }

    isSavingDialog.value = true
    try {
        const payload: MDBPrismeCaptureCapteur = {
            id: form.value.id,
            thingsBoardId: form.value.thingsBoardId.trim(),
            nom: form.value.nom.trim(),
            dernièreLatitude: form.value.dernièreLatitude ?? 0,
            dernièreLongitude: form.value.dernièreLongitude ?? 0,
            dernierChampId: form.value.dernierChampId,
            dernierChampNom: form.value.dernierChampNom,
            dernierClientNom: selectedClient.value ?? '',
            dernierRangOrdre: form.value.dernierRangOrdre,
            désactivé: form.value.désactivé,
            cultures: form.value.cultures,
        }

        if (isEditMode.value) {
            await apiProphet.put(`prsm/${siteCS.value}/captures/capteurs/${form.value.id}`, payload)
            Notify.success('Capteur modifié avec succès.')
        } else {
            const { id: _id, ...createPayload } = payload
            await apiProphet.post(`prsm/${siteCS.value}/captures/capteurs`, { ...createPayload, id: '' })
            Notify.success('Capteur créé avec succès.')
        }

        showDialog.value = false
        await getData()
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingDialog.value = false
    }
}

// ── Lifecycle ────────────────────────────────────────────────────────────
onMounted(() => getData())
watch(siteCS, () => getData())

// ── Reorder / Rang timeline ──────────────────────────────────────────────
const activeTab = ref<'liste' | 'ordre'>('liste')
const reorderList = ref<MDBPrismeCaptureCapteur[]>([])
const reorderDirty = ref(false)
const isSavingReorder = ref(false)
let dragFromIdx = -1
const dragOverIdx = ref(-1)

// Items that need saving — tracked individually so we only PUT what changed.
const pendingRankUpdates = ref<Map<string, number>>(new Map())

watch(activeTab, (tab) => {
    if (tab === 'ordre') {
        reorderList.value = [...capteurs.value].filter(c => !c.désactivé).sort((a, b) => a.dernierRangOrdre - b.dernierRangOrdre)
        reorderDirty.value = false
        pendingRankUpdates.value = new Map()
    }
})

function onDragStart(index: number) {
    dragFromIdx = index
}

function onDragOver(index: number) {
    if (dragFromIdx === -1 || dragFromIdx === index) return
    dragOverIdx.value = index
    const list = [...reorderList.value]
    const [moved] = list.splice(dragFromIdx, 1)
    list.splice(index, 0, moved)
    reorderList.value = list
    dragFromIdx = index
}

function onDragEnd() {
    if (dragFromIdx !== -1) return // still dragging
    dragOverIdx.value = -1
}

// Called when the browser fires dragend (mouse released)
function onDragFinished(finalIdx: number) {
    dragFromIdx = -1
    dragOverIdx.value = -1

    const list = reorderList.value
    const moved = list[finalIdx]
    if (!moved) return

    const prevRank = finalIdx > 0 ? list[finalIdx - 1].dernierRangOrdre : 0
    const nextRank = finalIdx < list.length - 1 ? list[finalIdx + 1].dernierRangOrdre : (list[finalIdx - 1]?.dernierRangOrdre ?? 0) + 200

    const gap = nextRank - prevRank

    if (gap > 1) {
        // Plenty of room — just bisect, touch only this item
        const newRank = Math.floor(prevRank + gap / 2)
        reorderList.value[finalIdx] = { ...moved, dernierRangOrdre: newRank }
        pendingRankUpdates.value.set(moved.id, newRank)
    } else {
        // No room — renormalize the whole list to 100, 200, 300…
        reorderList.value = list.map((c, i) => ({ ...c, dernierRangOrdre: (i + 1) * 100 }))
        for (const item of reorderList.value) {
            const original = capteurs.value.find(c => c.id === item.id)
            if (!original || original.dernierRangOrdre !== item.dernierRangOrdre)
                pendingRankUpdates.value.set(item.id, item.dernierRangOrdre)
        }
    }

    reorderDirty.value = pendingRankUpdates.value.size > 0
}

async function saveReorder() {
    isSavingReorder.value = true
    try {
        const toSave = reorderList.value.filter(c => pendingRankUpdates.value.has(c.id))
        await Promise.all(toSave.map(c =>
            apiProphet.put(`prsm/${siteCS.value}/captures/capteurs/${c.id}`, c)
        ))
        Notify.success(`Ordre enregistré (${toSave.length} mise${toSave.length > 1 ? 's' : ''} à jour).`)
        pendingRankUpdates.value = new Map()
        reorderDirty.value = false
        await getData()
        reorderList.value = [...capteurs.value].filter(c => !c.désactivé).sort((a, b) => a.dernierRangOrdre - b.dernierRangOrdre)
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingReorder.value = false
    }
}

// ── Table headers ─────────────────────────────────────────────────────────
const headers = [
    { title: 'Nom', key: 'nom', sortable: true },
    { title: 'Ferme', key: 'dernierClientNom', sortable: true },
    { title: 'Champ', key: 'dernierChampNom', sortable: true },
    { title: 'Rang', key: 'dernierRangOrdre', sortable: true },
    { title: 'Cultures', key: 'cultures', sortable: false },
    { title: 'État', key: 'désactivé', sortable: true },
    { title: 'Actions', key: 'actions', sortable: false, align: 'end' as const },
]
</script>

<template>
    <DialogConfirm ref="dialogConfirm"/>

    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 85vh; display: flex; flex-direction: column;">

        <!-- ── Page header ─────────────────────────────────────────────── -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

                <!-- Left: icon + title -->
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M12 7.5C12.69 7.5 13.27 7.73 13.76 8.2S14.5 9.27 14.5 10C14.5 11.05 14 11.81 13 12.28V21H11V12.28C10 11.81 9.5 11.05 9.5 10C9.5 9.27 9.76 8.67 10.24 8.2S11.31 7.5 12 7.5M16.69 5.3C17.94 6.55 18.61 8.11 18.7 10C18.7 11.8 18.03 13.38 16.69 14.72L15.5 13.5C16.5 12.59 17 11.42 17 10C17 8.67 16.5 7.5 15.5 6.5L16.69 5.3M6.09 4.08C4.5 5.67 3.7 7.64 3.7 10S4.5 14.3 6.09 15.89L4.92 17.11C3 15.08 2 12.7 2 10C2 7.3 3 4.94 4.92 2.91L6.09 4.08M19.08 2.91C21 4.94 22 7.3 22 10C22 12.8 21 15.17 19.08 17.11L17.91 15.89C19.5 14.3 20.3 12.33 20.3 10S19.5 5.67 17.91 4.08L19.08 2.91M7.31 5.3L8.5 6.5C7.5 7.42 7 8.58 7 10C7 11.33 7.5 12.5 8.5 13.5L7.31 14.72C5.97 13.38 5.3 11.8 5.3 10C5.3 8.2 5.97 6.64 7.31 5.3Z" size="26"/>
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Capteurs de spores</div>
                        <div class="text-caption text-medium-emphasis">Gestion des capteurs Prisme</div>
                    </div>
                </div>

                <!-- Right: add button -->
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        v-if="canEdit"
                        color="primary"
                        variant="tonal"
                        rounded="pill"
                        prepend-icon="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"
                        :disabled="isLoading"
                        @click="openCreate">
                        Ajouter
                    </v-btn>
                </div>
            </div>

            <!-- Second row: filter -->
            <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
                <v-text-field
                    v-model="filter"
                    label="Rechercher"
                    density="compact"
                    variant="outlined"
                    rounded="lg"
                    hide-details
                    prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                    clearable
                    style="max-width: 300px;"/>
                <v-chip
                    :color="showDésactivés ? 'warning' : 'default'"
                    :variant="showDésactivés ? 'flat' : 'tonal'"
                    prepend-icon="M2,5.27L3.28,4L20,20.72L18.73,22L15.65,18.92C14.5,19.3 13.28,19.5 12,19.5C7,19.5 2.73,16.39 1,12C1.69,10.24 2.79,8.69 4.19,7.46L2,5.27M12,9A3,3 0 0,1 15,12C15,12.35 14.94,12.69 14.83,13L11,9.17C11.31,9.06 11.65,9 12,9M12,4.5C17,4.5 21.27,7.61 23,12C22.18,14.08 20.79,15.88 19,17.19L17.58,15.76C18.94,14.82 20.06,13.54 20.82,12C19.17,8.64 15.76,6.5 12,6.5C10.91,6.5 9.84,6.68 8.84,7L7.3,5.47C8.74,4.85 10.33,4.5 12,4.5M3.18,12C4.83,15.36 8.24,17.5 12,17.5C12.69,17.5 13.37,17.43 14,17.29L11.72,15C10.29,14.85 9.15,13.71 9,12.28L5.6,8.87C4.61,9.72 3.78,10.78 3.18,12Z"
                    size="small"
                    label
                    style="cursor: pointer;"
                    @click="showDésactivés = !showDésactivés">
                    Afficher les désactivés
                </v-chip>
            </div>
        </div>

        <v-divider/>

        <!-- ── Tabs ─────────────────────────────────────────────────────── -->
        <v-tabs v-model="activeTab" density="compact" class="px-2 pt-1" color="primary">
            <v-tab value="liste" prepend-icon="M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A1.5,1.5 0 0,1 5.5,6A1.5,1.5 0 0,1 4,7.5A1.5,1.5 0 0,1 2.5,6A1.5,1.5 0 0,1 4,4.5M4,10.5A1.5,1.5 0 0,1 5.5,12A1.5,1.5 0 0,1 4,13.5A1.5,1.5 0 0,1 2.5,12A1.5,1.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A1.5,1.5 0 0,1 5.5,18A1.5,1.5 0 0,1 4,19.5A1.5,1.5 0 0,1 2.5,18A1.5,1.5 0 0,1 4,16.5Z">Liste</v-tab>
            <v-tab value="ordre" prepend-icon="M19 17H22L18 21L14 17H17V3H19V17M9 13H7C5.9 13 5 13.9 5 15V16C5 17.11 5.9 18 7 18H9V19H5V21H9C10.11 21 11 20.11 11 19V15C11 13.9 10.11 13 9 13M9 16H7V15H9V16M9 3H7C5.9 3 5 3.9 5 5V9C5 10.11 5.9 11 7 11H9C10.11 11 11 10.11 11 9V5C11 3.9 10.11 3 9 3M9 9H7V5H9V9Z">Ordre / Rang</v-tab>
        </v-tabs>

        <v-divider/>

        <!-- ── Content ──────────────────────────────────────────────────── -->
        <div style="flex: 1 1 auto; overflow: auto;">
            <v-window v-model="activeTab">
            <v-window-item value="liste">
            <v-data-table
                :headers="headers"
                :items="filteredCapteurs"
                :hide-default-header="display.smAndDown.value"
                item-value="id"
                density="comfortable"
                hover>

                <!-- Mobile card rows -->
                <template v-if="display.smAndDown.value" #item="{ item }">
                    <div class="capteur-mobile-card ma-2 pa-3">
                        <div class="d-flex align-center justify-space-between mb-2">
                            <div style="min-width: 0;">
                                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                                    <span class="text-subtitle-2 font-weight-bold text-truncate">{{ item.nom }}</span>
                                    <v-chip v-if="item.désactivé" color="warning" size="x-small" variant="flat" prepend-icon="M12,3A9,9 0 0,0 3,12A9,9 0 0,0 12,21A9,9 0 0,0 21,12A9,9 0 0,0 12,3M12,19A7,7 0 0,1 5,12A7,7 0 0,1 12,5A7,7 0 0,1 19,12A7,7 0 0,1 12,19Z">Désactivé</v-chip>
                                </div>
                            </div>
                            <v-btn
                                v-if="canEdit"
                                icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"
                                variant="tonal"
                                color="primary"
                                size="small"
                                class="flex-shrink-0 ms-2"
                                @click="openEdit(item)"/>
                        </div>
                        <v-divider class="mb-2"/>
                        <div class="d-flex align-center" style="gap: 4px;">
                            <v-icon icon="M18,15H16V17H18M18,11H16V13H18M20,19H12V17H14V15H12V13H14V11H12V9H20M10,7H8V5H10M10,11H8V9H10M10,15H8V13H10M10,19H8V17H10M6,7H4V5H6M6,11H4V9H6M6,15H4V13H6M6,19H4V17H6M12,7V3H2V21H22V7H12Z" size="14" color="medium-emphasis"/>
                            <span class="text-caption text-medium-emphasis text-truncate">
                                {{ item.dernierClientNom || '—' }}
                            </span>
                        </div>
                        <div class="d-flex align-center mt-1" style="gap: 4px;">
                            <v-icon icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z" size="14" color="medium-emphasis"/>
                            <span class="text-caption text-medium-emphasis text-truncate">
                                {{ item.dernierChampNom || '—' }}
                            </span>
                        </div>
                        <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                            <v-icon icon="M8.04 16.34C9.05 13.83 10.19 10.96 14.5 10C14.5 10 9.5 10 7.88 14.63C7.88 14.63 7 13.75 7 12.75S8 9.63 10.5 9.13C11.21 9 12 8.87 12.78 8.76C14.75 8.5 16.64 8.22 17 7.5C17 7.5 15.5 16 10 16C9.82 16 9.57 15.94 9.33 15.85L8.86 17L7.91 16.67L8.04 16.34M12 4C16.41 4 20 7.59 20 12S16.41 20 12 20 4 16.41 4 12 7.59 4 12 4M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2Z" size="14" color="medium-emphasis" class="flex-shrink-0"/>
                            <span v-if="item.cultures.length === 0" class="text-caption text-medium-emphasis">Toutes les cultures</span>
                            <template v-else>
                                <v-chip v-for="c in item.cultures" :key="c" size="x-small" color="teal" variant="tonal">{{ c }}</v-chip>
                            </template>
                        </div>
                    </div>
                </template>

                <!-- Ferme -->
                <template #item.dernierClientNom="{ item }">
                    <span class="text-caption text-medium-emphasis">
                        {{ item.dernierClientNom || '—' }}
                    </span>
                </template>

                <!-- Champ -->
                <template #item.dernierChampNom="{ item }">
                    <span class="text-caption text-medium-emphasis">
                        {{ item.dernierChampNom || '—' }}
                    </span>
                </template>

                <!-- Cultures -->
                <template #item.cultures="{ item }">
                    <span v-if="item.cultures.length === 0" class="text-caption text-medium-emphasis">Toutes</span>
                    <div v-else class="d-flex flex-wrap" style="gap: 4px;">
                        <v-chip v-for="c in item.cultures" :key="c" size="x-small" color="teal" variant="tonal">{{ c }}</v-chip>
                    </div>
                </template>

                <!-- État -->
                <template #item.désactivé="{ item }">
                    <v-chip
                        v-if="item.désactivé"
                        color="warning"
                        size="x-small"
                        variant="flat"
                        prepend-icon="M12,3A9,9 0 0,0 3,12A9,9 0 0,0 12,21A9,9 0 0,0 21,12A9,9 0 0,0 12,3M12,19A7,7 0 0,1 5,12A7,7 0 0,1 12,5A7,7 0 0,1 19,12A7,7 0 0,1 12,19Z">
                        Désactivé
                    </v-chip>
                    <v-chip
                        v-else
                        color="success"
                        size="x-small"
                        variant="tonal"
                        prepend-icon="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z">
                        Actif
                    </v-chip>
                </template>

                <!-- Actions -->
                <template #item.actions="{ item }">
                    <v-btn
                        v-if="canEdit"
                        icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"
                        variant="text"
                        size="small"
                        color="primary"
                        @click="openEdit(item)"/>
                </template>

                <!-- Empty state -->
                <template #no-data>
                    <div class="text-center py-8 text-medium-emphasis">
                        <v-icon icon="M12 7.5C12.69 7.5 13.27 7.73 13.76 8.2S14.5 9.27 14.5 10C14.5 11.05 14 11.81 13 12.28V21H11V12.28C10 11.81 9.5 11.05 9.5 10C9.5 9.27 9.76 8.67 10.24 8.2S11.31 7.5 12 7.5M16.69 5.3C17.94 6.55 18.61 8.11 18.7 10C18.7 11.8 18.03 13.38 16.69 14.72L15.5 13.5C16.5 12.59 17 11.42 17 10C17 8.67 16.5 7.5 15.5 6.5L16.69 5.3M6.09 4.08C4.5 5.67 3.7 7.64 3.7 10S4.5 14.3 6.09 15.89L4.92 17.11C3 15.08 2 12.7 2 10C2 7.3 3 4.94 4.92 2.91L6.09 4.08M19.08 2.91C21 4.94 22 7.3 22 10C22 12.8 21 15.17 19.08 17.11L17.91 15.89C19.5 14.3 20.3 12.33 20.3 10S19.5 5.67 17.91 4.08L19.08 2.91M7.31 5.3L8.5 6.5C7.5 7.42 7 8.58 7 10C7 11.33 7.5 12.5 8.5 13.5L7.31 14.72C5.97 13.38 5.3 11.8 5.3 10C5.3 8.2 5.97 6.64 7.31 5.3Z" size="40" class="mb-2"/>
                        <div>Aucun capteur trouvé</div>
                    </div>
                </template>
            </v-data-table>
            </v-window-item>

            <!-- ── Ordre / Rang timeline ─────────────────────────────── -->
            <v-window-item value="ordre">
                <div class="pa-4 pa-sm-6">
                    <!-- Unsaved banner -->
                    <v-alert
                        v-if="reorderDirty"
                        type="info"
                        variant="tonal"
                        density="compact"
                        rounded="lg"
                        class="mb-4">
                        <template #default>
                            Ordre modifié — enregistrez pour appliquer.
                        </template>
                        <template #append>
                            <v-btn
                                v-if="canEdit"
                                color="primary"
                                variant="flat"
                                size="small"
                                rounded="pill"
                                prepend-icon="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z"
                                :loading="isSavingReorder"
                                @click="saveReorder">
                                Enregistrer
                            </v-btn>
                        </template>
                    </v-alert>

                    <div class="text-caption text-medium-emphasis mb-5 d-flex align-center" style="gap: 4px;">
                        <v-icon icon="M13,6V11H18V7.75L22.25,12L18,16.25V13H13V18H16.25L12,22.25L7.75,18H11V13H6V16.25L1.75,12L6,7.75V11H11V6H7.75L12,1.75L16.25,6H13Z" size="14"/>
                        Glissez les capteurs pour modifier leur rang
                    </div>

                    <!-- Timeline strip -->
                    <div
                        class="reorder-track"
                        :class="display.smAndDown.value ? 'reorder-track--vertical' : 'reorder-track--horizontal'">

                        <div
                            v-for="(capteur, i) in reorderList"
                            :key="capteur.id"
                            class="reorder-item"
                            :class="{ 'reorder-item--dragover': dragOverIdx === i }"
                            :draggable="canEdit"
                            @dragstart="canEdit && onDragStart(i)"
                            @dragover.prevent="canEdit && onDragOver(i)"
                            @dragend="canEdit && onDragFinished(i)">

                            <!-- Rank badge -->
                            <div class="reorder-rank">{{ i }}</div>

                            <!-- Card -->
                            <v-card
                                class="reorder-card"
                                variant="outlined"
                                rounded="lg"
                                :class="{ 'reorder-card--dragover': dragOverIdx === i }">
                                <v-card-text class="pa-2 d-flex align-center" style="gap: 6px;">
                                    <v-icon
                                        v-if="canEdit"
                                        icon="M9,3H11V5H9V3M13,3H15V5H13V3M9,7H11V9H9V7M13,7H15V9H13V7M9,11H11V13H9V11M13,11H15V13H13V11M9,15H11V17H9V15M13,15H15V17H13V15M9,19H11V21H9V19M13,19H15V21H13V19Z"
                                        color="medium-emphasis"
                                        size="20"
                                        class="drag-handle flex-shrink-0"/>
                                    <div style="min-width: 0; flex: 1;">
                                        <div class="text-body-2 font-weight-medium text-truncate">{{ capteur.nom }}</div>
                                        <div class="text-caption text-medium-emphasis text-truncate">{{ capteur.dernierClientNom || '—' }} · {{ capteur.dernierChampNom || '—' }}</div>
                                    </div>
                                </v-card-text>
                            </v-card>

                            <!-- Connector arrow (horizontal only) -->
                            <v-icon
                                v-if="i < reorderList.length - 1 && !display.smAndDown.value"
                                icon="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"
                                size="18"
                                color="medium-emphasis"/>
                        </div>
                    </div>

                    <!-- Save button at bottom -->
                    <div v-if="canEdit && reorderDirty" class="d-flex justify-end mt-5">
                        <v-btn
                            color="primary"
                            variant="flat"
                            rounded="pill"
                            prepend-icon="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z"
                            :loading="isSavingReorder"
                            @click="saveReorder">
                            Enregistrer l'ordre
                        </v-btn>
                    </div>
                </div>
            </v-window-item>

            </v-window>
        </div>
    </v-card>

    <!-- ── Create / Edit dialog ──────────────────────────────────────────── -->
    <v-dialog
        v-model="showDialog"
        :fullscreen="display.smAndDown.value"
        :max-width="display.smAndDown.value ? undefined : 640"
        scrollable>
        <v-card>
            <v-card-title class="d-flex align-center pa-4" style="gap: 10px;">
                <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                    <v-icon :icon="isEditMode ? 'M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z' : 'M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z'" size="20"/>
                </v-avatar>
                <span class="text-h6">{{ isEditMode ? 'Modifier le capteur' : 'Ajouter un capteur' }}</span>
                <v-spacer/>
                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="showDialog = false"/>
            </v-card-title>

            <v-divider/>

            <v-card-text class="pa-4">
                <v-container class="pa-0">
                    <v-row>
                        <!-- Nom -->
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.nom"
                                label="Nom du capteur *"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="M16,17H5V7H16L19.55,12M17.63,5.84C17.27,5.33 16.67,5 16,5H5A2,2 0 0,0 3,7V17A2,2 0 0,0 5,19H16C16.67,19 17.27,18.66 17.63,18.15L22,12L17.63,5.84Z"
                                hide-details="auto"/>
                        </v-col>

                        <!-- Rang ordre -->
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model.number="form.dernierRangOrdre"
                                label="Ordre dans le rang"
                                type="number"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="M4,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M4,6V18H11V6H4M20,18V6H18.76C19,6.54 18.95,7.07 18.95,7.13C18.88,7.8 18.41,8.5 18.24,8.75L15.91,11.3L19.23,11.28L19.24,12.5L14.04,12.47L14,11.47C14,11.47 17.05,8.24 17.2,7.95C17.34,7.67 17.91,6 16.5,6C15.27,6.05 15.41,7.3 15.41,7.3L13.87,7.31C13.87,7.31 13.88,6.65 14.25,6H13V18H15.58L15.57,17.14L16.54,17.13C16.54,17.13 17.45,16.97 17.46,16.08C17.5,15.08 16.65,15.08 16.5,15.08C16.37,15.08 15.43,15.13 15.43,15.95H13.91C13.91,15.95 13.95,13.89 16.5,13.89C19.1,13.89 18.96,15.91 18.96,15.91C18.96,15.91 19,17.16 17.85,17.63L18.37,18H20M8.92,16H7.42V10.2L5.62,10.76V9.53L8.76,8.41H8.92V16Z"
                                hide-details="auto"
                                min="0"/>
                        </v-col>

                        <!-- Client selector -->
                        <v-col cols="12" sm="6">
                            <v-autocomplete
                                v-model="selectedClient"
                                :items="clients"
                                label="Client (pour champ)"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6M12,13C14.67,13 20,14.33 20,17V20H4V17C4,14.33 9.33,13 12,13M12,14.9C9.03,14.9 5.9,16.36 5.9,17V18.1H18.1V17C18.1,16.36 14.97,14.9 12,14.9Z"
                                hide-details="auto"
                                clearable
                                no-data-text="Aucun client trouvé"/>
                        </v-col>

                        <!-- Champ (autocomplete + saisie libre) -->
                        <v-col cols="12">
                            <v-combobox
                                v-model="champValue"
                                :items="champSuggestions"
                                :item-title="(c: MDBPrismeChamp | string) => typeof c === 'string' ? c : c.nom"
                                :item-value="(c: MDBPrismeChamp | string) => c"
                                :loading="isLoadingChamps || isLocatingNearby"
                                label="Champ"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                :prepend-inner-icon="isLocatingNearby ? 'M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z' : 'M15.5,12C18,12 20,14 20,16.5C20,17.38 19.75,18.2 19.31,18.9L22.39,22L21,23.39L17.88,20.32C17.19,20.75 16.37,21 15.5,21C13,21 11,19 11,16.5C11,14 13,12 15.5,12M15.5,14A2.5,2.5 0 0,0 13,16.5A2.5,2.5 0 0,0 15.5,19A2.5,2.5 0 0,0 18,16.5A2.5,2.5 0 0,0 15.5,14M19.5,2A0.5,0.5 0 0,1 20,2.5V11.81C19.42,11.26 18.75,10.81 18,10.5V4.7L15,5.86V10C14.3,10.07 13.62,10.24 13,10.5V5.87L9,4.47V16.13H9V16.5C9,17.14 9.09,17.76 9.26,18.34L8,17.9L2.66,19.97L2.5,20A0.5,0.5 0 0,1 2,19.5V4.38C2,4.15 2.15,3.97 2.36,3.9L8,2L14,4.1L19.34,2.03L19.5,2M4,5.46V17.31L7,16.15V4.45L4,5.46Z'"
                                hide-details="auto"
                                clearable
                                return-object
                                no-data-text="Tapez pour chercher ou saisir un nom libre"
                                no-filter
                                @update:search="onChampInput"
                                @update:model-value="onChampSelect">
                                <template #item="{ props, item }">
                                    <v-list-item v-bind="props" :subtitle="typeof item.raw === 'string' ? '' : item.raw.id.slice(-8)"/>
                                </template>
                            </v-combobox>
                            <div v-if="isLocatingNearby" class="text-caption text-medium-emphasis mt-1 ms-2 d-flex align-center" style="gap: 4px;">
                                <v-icon icon="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z" size="12"/>
                                Recherche du champ le plus proche…
                            </div>
                            <div v-else-if="form.dernierChampId" class="text-caption text-medium-emphasis mt-1 ms-2">
                                ID champ: {{ form.dernierChampId }}
                            </div>
                        </v-col>

                        <!-- ThingsBoard ID -->
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.thingsBoardId"
                                label="Identifiant ThingsBoard"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="M10 7V9H9V15H10V17H6V15H7V9H6V7H10M16 7C17.11 7 18 7.9 18 9V15C18 16.11 17.11 17 16 17H12V7M16 9H14V15H16V9Z"
                                hide-details="auto"/>
                        </v-col>

                        <!-- Cultures -->
                        <v-col cols="12">
                            <v-select
                                v-model="form.cultures"
                                :items="culturesDisponibles"
                                label="Cultures"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                multiple
                                chips
                                closable-chips
                                prepend-inner-icon="M8.04 16.34C9.05 13.83 10.19 10.96 14.5 10C14.5 10 9.5 10 7.88 14.63C7.88 14.63 7 13.75 7 12.75S8 9.63 10.5 9.13C11.21 9 12 8.87 12.78 8.76C14.75 8.5 16.64 8.22 17 7.5C17 7.5 15.5 16 10 16C9.82 16 9.57 15.94 9.33 15.85L8.86 17L7.91 16.67L8.04 16.34M12 4C16.41 4 20 7.59 20 12S16.41 20 12 20 4 16.41 4 12 7.59 4 12 4M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2Z"
                                hide-details="auto"/>
                            <div class="text-caption text-medium-emphasis mt-1 ms-2">
                                Vide = associé à toutes les cultures
                            </div>
                        </v-col>

                        <!-- Désactivé -->
                        <v-col v-if="isEditMode" cols="12">
                            <v-switch
                                :model-value="!form.désactivé"
                                color="success"
                                hide-details
                                density="compact"
                                inset
                                @update:model-value="form.désactivé = !$event">
                                <template #label>
                                    <div class="d-flex align-center" style="gap: 6px;">
                                        <v-icon :icon="form.désactivé ? 'M12,3A9,9 0 0,0 3,12A9,9 0 0,0 12,21A9,9 0 0,0 21,12A9,9 0 0,0 12,3M12,19A7,7 0 0,1 5,12A7,7 0 0,1 12,5A7,7 0 0,1 19,12A7,7 0 0,1 12,19Z' : 'M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z'" :color="form.désactivé ? 'warning' : 'success'" size="18"/>
                                        <span>{{ form.désactivé ? 'Capteur désactivé' : 'Capteur actif' }}</span>
                                    </div>
                                </template>
                            </v-switch>
                        </v-col>

                        <!-- ── Coordinates ─────────────────────────────── -->
                        <v-col cols="12">
                            <div class="d-flex align-center justify-space-between mb-2" style="gap: 8px;">
                                <div class="text-body-2 font-weight-medium">Coordonnées GPS</div>
                                <v-btn
                                    color="secondary"
                                    variant="tonal"
                                    size="small"
                                    rounded="pill"
                                    prepend-icon="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z"
                                    :loading="isLocating"
                                    @click="locateMe">
                                    Me localiser
                                </v-btn>
                            </div>

                            <!-- Lat / Lng manual inputs -->
                            <v-row dense class="mb-3">
                                <v-col cols="6">
                                    <v-text-field
                                        v-model.number="form.dernièreLatitude"
                                        label="Latitude"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        rounded="lg"
                                        hide-details="auto"
                                        step="0.000001"/>
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field
                                        v-model.number="form.dernièreLongitude"
                                        label="Longitude"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        rounded="lg"
                                        hide-details="auto"
                                        step="0.000001"/>
                                </v-col>
                            </v-row>

                            <!-- Mapbox picker -->
                            <div class="map-container" style="position: relative; border-radius: 12px; overflow: hidden;">
                                <div class="text-caption text-medium-emphasis map-hint pa-1">
                                    <v-icon icon="M10,9A1,1 0 0,1 11,8A1,1 0 0,1 12,9V13.47L13.21,13.6L18.15,15.79C18.68,16.03 19,16.56 19,17.14V21.5C18.97,22.32 18.32,22.97 17.5,23H11C10.62,23 10.26,22.85 10,22.57L5.1,18.37L5.84,17.6C6.03,17.39 6.3,17.28 6.58,17.28H6.8L10,19V9M11,5A4,4 0 0,1 15,9C15,10.5 14.2,11.77 13,12.46V11.24C13.61,10.69 14,9.89 14,9A3,3 0 0,0 11,6A3,3 0 0,0 8,9C8,9.89 8.39,10.69 9,11.24V12.46C7.8,11.77 7,10.5 7,9A4,4 0 0,1 11,5Z" size="14"/> Cliquez sur la carte pour placer le capteur
                                </div>
                                <v-btn
                                    :color="showChampsOverlay ? 'success' : 'surface'"
                                    :variant="showChampsOverlay ? 'flat' : 'elevated'"
                                    size="x-small"
                                    rounded="pill"
                                    :prepend-icon="showChampsOverlay ? 'M12,16L19.36,10.27L21,9L12,2L3,9L4.63,10.27M12,18.54L4.62,12.81L3,14.07L12,21.07L21,14.07L19.37,12.8L12,18.54Z' : 'M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z'"
                                    :loading="isLoadingGeoJson"
                                    elevation="3"
                                    style="position: absolute; top: 36px; right: 8px; z-index: 1;"
                                    @click.stop="toggleChampsOverlay">
                                    Champs
                                </v-btn>
                                <MapboxMap
                                    style="width: 100%; height: 260px;"
                                    :map-style="theme.global.current.value.dark
                                        ? 'mapbox://styles/mapbox/dark-v11'
                                        : 'mapbox://styles/mapbox/satellite-streets-v12'"
                                    :center="mapCenter"
                                    :zoom="11"
                                    :access-token="MAPBOX_TOKEN"
                                    @mb-click="onMapClick">
                                    <template v-if="showChampsOverlay && champsGeoJson">
                                        <MapboxLayer id="capteur-champs-fill" :options="champsFillLayerOptions"/>
                                        <MapboxLayer id="capteur-champs-outline" :options="champsOutlineLayerOptions"/>
                                        <MapboxLayer id="capteur-champs-labels" :options="champsLabelLayerOptions"/>
                                        <MapboxLayer id="capteur-champs-group-labels" :options="champsGroupLabelLayerOptions"/>
                                    </template>
                                    <MapboxMarker
                                        v-if="markerCoords"
                                        :lng-lat="markerCoords"
                                        color="#1976D2"/>
                                </MapboxMap>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider/>

            <v-card-actions class="pa-4">
                <v-spacer/>
                <v-btn variant="text" @click="showDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="pill"
                    prepend-icon="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z"
                    :loading="isSavingDialog"
                    @click="saveDialog">
                    {{ isEditMode ? 'Enregistrer' : 'Créer' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Mobile card ─────────────────────────────── */
.capteur-mobile-card {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)"');
    border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"');
    border-radius: 12px;
}

/* ── Map hint overlay ────────────────────────── */
.map-container {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.map-hint {
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    border-radius: 20px;
    pointer-events: none;
    z-index: 10;
    white-space: nowrap;
}

/* ── Reorder timeline ────────────────────────── */
.reorder-track--horizontal {
    display: flex;
    align-items: flex-start;
    overflow-x: auto;
    padding-bottom: 8px;
    gap: 0;
}

.reorder-track--vertical {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.reorder-item {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    transition: opacity 0.15s;
}

.reorder-item--dragover {
    opacity: 0.5;
}

.reorder-rank {
    min-width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(var(--v-theme-primary), 0.15);
    color: rgb(var(--v-theme-primary));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}

.reorder-card {
    min-width: 140px;
    max-width: 200px;
}

.reorder-card--dragover {
    border-color: rgb(var(--v-theme-primary)) !important;
    background: rgba(var(--v-theme-primary), 0.08) !important;
}

.drag-handle {
    cursor: grab;
    opacity: 0.5;
}

.drag-handle:active {
    cursor: grabbing;
}
</style>
